import React from 'react';
import { Col, Row } from 'antd';
import { TaskHelpers, Common } from 'ontraccr-common';
import { Duration, DateTime } from 'luxon';

import { TextRow } from '../clock/ManualEntry/manualEntryHelpers';

export default {};

export const sortByStartTime = (a, b) => (
  (TaskHelpers.getStartTime(a)) - (TaskHelpers.getStartTime(b))
);

export const sortByNameAndStartTime = (userMap) => (a, b) => {
  const userA = userMap?.[a?.userId]?.name ?? '';
  const userB = userMap?.[b?.userId]?.name ?? '';
  const userComparison = userA.localeCompare(userB);

  if (userComparison === 0) {
    return sortByStartTime(a, b);
  }

  return userComparison;
};

export const getEnteredViaText = ({
  textFlag = null,
  metadata,
}) => {
  if (!metadata) return null;

  const {
    deviceText,
    submitterText,
  } = metadata;

  const title = textFlag ? `${textFlag}` : '';
  const text = `${submitterText} (${deviceText})`;

  return (
    <Row style={{ height: '100%' }}>
      <Col style={{ fontSize: 12, height: '100%' }}>
        <Row style={{ height: '100%' }}>
          <Col style={{ height: '100%' }}>
            {title ? (
              <TextRow title={title} text={text} />
            ) : (
              <Row
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 200,
                  textWrap: 'wrap',
                  height: '100%',
                }}
              >
                {text}
              </Row>
            )}
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export const getEnteredViaTableText = (metadata) => {
  const {
    entered,
    start,
    end,
  } = metadata ?? {};

  if (entered) {
    return getEnteredViaText({ metadata: entered });
  }

  const startText = getEnteredViaText({ metadata: start, textFlag: 'Start' });

  const endText = getEnteredViaText({ metadata: end, textFlag: 'End' });

  return (
    <div>
      {startText}
      {endText}
    </div>
  );
};

export const getTaskDurationText = ({
  startTime,
  endTime,
} = {}, {
  showAbbreviatedDuration = true,
  showZeroTimes = false,
} = {}) => {
  if (Common.isNullOrUndefined(endTime) || Common.isNullOrUndefined(startTime)) return null;

  const duration = Duration.fromMillis(endTime - startTime).shiftTo('hours', 'minutes');
  let text = '';
  const {
    hours,
    minutes,
  } = duration.toObject();
  const hInt = parseInt(hours, 10);
  const mInt = parseInt(minutes, 10);

  const renderAllTimes = showZeroTimes && hInt === 0 && mInt === 0;
  if (hInt > 0 || renderAllTimes) text += `${hInt}${showAbbreviatedDuration ? 'h' : ' hour(s)'}`;
  if (mInt > 0 || renderAllTimes) {
    if (text.length > 0) text += ' ';
    text += `${mInt}${showAbbreviatedDuration ? 'm' : ' minute(s)'}`;
  }
  return text;
};

export const getDisplayStart = (task = {}, showOriginalTimes = false) => (
  showOriginalTimes && task?.originalStart
    ? task?.originalStart
    : task?.startTime ?? null
);

export const getDisplayEnd = (task = {}, showOriginalTimes = false) => (
  showOriginalTimes && task?.originalEnd
    ? task?.originalEnd
    : task?.endTime ?? null
);

const calculateHourBasedTimeHelper = ({
  startTime,
  endTime,
  startTimeKey,
  endTimeKey,
  hoursKey,
  date,
  timezone,
}) => {
  if (!startTime || !endTime) return {};
  const duration = Duration.fromMillis(endTime - startTime);
  // Round to whole minutes so leftover seconds don't become fractional
  // minutes (Luxon set() truncates them, minus() does not).
  const totalMinutes = Math.round(duration.as('minutes'));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Preserve sub-minute remainder used for hour-based entry ordering.
  const orderOffset = startTime % (60 * 1000);

  // Web hour-based: set end from wall-clock hours/minutes, then subtract
  // to get start. Using plus() would break DST (e.g. 4h from midnight on
  // spring-forward lands at 5AM and displays as 5 hours).
  const startOfDay = DateTime.fromSQL(date, { zone: timezone }).startOf('day');
  const newEndTime = startOfDay
    .set({ hours, minutes })
    .plus({ milliseconds: orderOffset });
  const newStartTime = newEndTime.minus({ hours, minutes });
  return {
    [startTimeKey]: newStartTime.toMillis(),
    [endTimeKey]: newEndTime.toMillis(),
    [hoursKey]: { hours, minutes },
  };
};

/**
 * Function to calculate the hour based times for a task
 * This is due to daylight savings time and the disparity between how mobile and web handles hour based entries
 * Web handles hour based entries by setting the end time to the hours/minutes and then subtracting to get start time
 * Mobile handles hour based entries by going to the start of the day and then adding the hours/minutes to get end time
 * This function calculates it how web expects it, to allow the correct value to show up in the hours input
 *
 * When enableManualOT is false (auto OT), the "hours" field shows total work time (reg + OT + DT)
 * because users can only edit the total — OT/DT distribution is recomputed by the backend on save.
 * If the entry has no regular segment (all hours redistributed to OT/DT), a synthetic regular span
 * is built from those segments so the hours input is still populated.
 * When enableManualOT is true (manual OT), OT/DT segments are kept separate for individual editing.
 */
export const calculateHourBasedTimes = ({
  startTime,
  endTime,
  breakStartTime,
  breakEndTime,
  otStartTime,
  otEndTime,
  doubleOTStartTime,
  doubleOTEndTime,
  ptoStartTime,
  ptoEndTime,
  date,
  timezone,
  enableManualOT,
}) => {
  let regStart = startTime;
  let regEnd = endTime;

  if (!enableManualOT) {
    const otMs = (otStartTime && otEndTime) ? otEndTime - otStartTime : 0;
    const doubleOTMs = (doubleOTStartTime && doubleOTEndTime)
      ? doubleOTEndTime - doubleOTStartTime
      : 0;
    const totalExtraMs = otMs + doubleOTMs;
    if (totalExtraMs > 0) {
      if (regStart && regEnd) {
        regEnd += totalExtraMs;
      } else {
        const syntheticStart = (otStartTime && otEndTime)
          ? otStartTime
          : doubleOTStartTime;
        regStart = syntheticStart;
        regEnd = syntheticStart + totalExtraMs;
      }
    }
  }

  const hasManualOTSegments = enableManualOT && !!(otStartTime && otEndTime);
  const hasManualDTSegments = enableManualOT && !!(doubleOTStartTime && doubleOTEndTime);

  const regTimes = calculateHourBasedTimeHelper({
    startTime: regStart,
    endTime: regEnd,
    startTimeKey: 'startTime',
    endTimeKey: 'endTime',
    hoursKey: 'hours',
    date,
    timezone,
  });
  const breakTimes = calculateHourBasedTimeHelper({
    startTime: breakStartTime,
    endTime: breakEndTime,
    startTimeKey: 'breakStartTime',
    endTimeKey: 'breakEndTime',
    hoursKey: 'breakHours',
    date,
    timezone,
  });
  const otTimes = hasManualOTSegments
    ? calculateHourBasedTimeHelper({
      startTime: otStartTime,
      endTime: otEndTime,
      startTimeKey: 'otStartTime',
      endTimeKey: 'otEndTime',
      hoursKey: 'otHours',
      date,
      timezone,
    })
    : { otStartTime: null, otEndTime: null, otHours: null };
  const doubleOTTimes = hasManualDTSegments
    ? calculateHourBasedTimeHelper({
      startTime: doubleOTStartTime,
      endTime: doubleOTEndTime,
      startTimeKey: 'doubleOTStartTime',
      endTimeKey: 'doubleOTEndTime',
      hoursKey: 'doubleOTHours',
      date,
      timezone,
    })
    : { doubleOTStartTime: null, doubleOTEndTime: null, doubleOTHours: null };
  const ptoTimes = calculateHourBasedTimeHelper({
    startTime: ptoStartTime,
    endTime: ptoEndTime,
    startTimeKey: 'ptoStartTime',
    endTimeKey: 'ptoEndTime',
    hoursKey: 'ptoHours',
    date,
    timezone,
  });
  return {
    ...regTimes,
    ...breakTimes,
    ...otTimes,
    ...doubleOTTimes,
    ...ptoTimes,
  };
};
